import { AppDownloadLink, DownloadLinkDuration } from '../types';

export const DURATION_OPTIONS: { id: DownloadLinkDuration; label: string; description: string; hours?: number }[] = [
  { id: '3_hours', label: '3 Horas', description: 'Acceso temporal de 3 horas' },
  { id: '1_day', label: '1 Día', description: 'Válido durante 24 horas' },
  { id: '1_week', label: '1 Semana', description: 'Válido durante 7 días' },
  { id: '1_month', label: '1 Mes', description: 'Válido durante 30 días' },
  { id: 'never', label: 'Nunca (Permanente)', description: 'Sin fecha de vencimiento' },
];

export function getDurationLabel(duration: DownloadLinkDuration): string {
  switch (duration) {
    case '3_hours':
      return '3 Horas';
    case '1_day':
      return '1 Día';
    case '1_week':
      return '1 Semana';
    case '1_month':
      return '1 Mes';
    case 'never':
      return 'Permanente (Nunca)';
    default:
      return duration;
  }
}

export function calculateExpirationDate(duration: DownloadLinkDuration): string | null {
  const now = Date.now();
  switch (duration) {
    case '3_hours':
      return new Date(now + 3 * 60 * 60 * 1000).toISOString();
    case '1_day':
      return new Date(now + 24 * 60 * 60 * 1000).toISOString();
    case '1_week':
      return new Date(now + 7 * 24 * 60 * 60 * 1000).toISOString();
    case '1_month':
      return new Date(now + 30 * 24 * 60 * 60 * 1000).toISOString();
    case 'never':
      return null;
  }
}

export function isDownloadLinkExpired(link: AppDownloadLink): boolean {
  if (!link.isActive) return true;
  if (!link.expiresAt) return false;
  return new Date(link.expiresAt).getTime() <= Date.now();
}

export function getRemainingTimeLabel(expiresAt: string | null): { text: string; isExpired: boolean; isPermanent: boolean } {
  if (!expiresAt) {
    return { text: 'Nunca expira (Permanente)', isExpired: false, isPermanent: true };
  }

  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (diffMs <= 0) {
    return { text: 'Enlace expirado', isExpired: true, isPermanent: false };
  }

  const totalMinutes = Math.floor(diffMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remHours = hours % 24;
    return {
      text: `Expira en ${days} día${days > 1 ? 's' : ''}${remHours > 0 ? ` y ${remHours}h` : ''}`,
      isExpired: false,
      isPermanent: false,
    };
  }

  if (hours > 0) {
    return {
      text: `Expira en ${hours} hora${hours > 1 ? 's' : ''} y ${minutes} min`,
      isExpired: false,
      isPermanent: false,
    };
  }

  return {
    text: `Expira en ${Math.max(1, minutes)} minuto${minutes > 1 ? 's' : ''}`,
    isExpired: false,
    isPermanent: false,
  };
}

export function getExpirationDetails(link: AppDownloadLink): {
  isExpired: boolean;
  status: 'valid' | 'expired' | 'revoked';
  statusText: string;
  expiredAgoText?: string;
  formattedExpiresAt?: string;
  remainingText?: string;
} {
  if (!link.isActive) {
    return {
      isExpired: true,
      status: 'revoked',
      statusText: 'Enlace desactivado',
      expiredAgoText: 'Este enlace fue desactivado o revocado por el administrador.',
    };
  }

  if (!link.expiresAt) {
    return {
      isExpired: false,
      status: 'valid',
      statusText: 'Permanente (nunca expira)',
      remainingText: 'Permanente (nunca expira)',
    };
  }

  const diffMs = new Date(link.expiresAt).getTime() - Date.now();
  if (diffMs <= 0) {
    const elapsedMs = Math.abs(diffMs);
    const totalMinutes = Math.floor(elapsedMs / (60 * 1000));
    const hours = Math.floor(totalMinutes / 60);
    const days = Math.floor(hours / 24);

    let expiredAgo = 'hace unos momentos';
    if (days > 0) {
      expiredAgo = `hace ${days} día${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      expiredAgo = `hace ${hours} hora${hours > 1 ? 's' : ''}`;
    } else if (totalMinutes > 0) {
      expiredAgo = `hace ${totalMinutes} minuto${totalMinutes > 1 ? 's' : ''}`;
    }

    let formattedDate = '';
    try {
      formattedDate = new Date(link.expiresAt).toLocaleString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {}

    return {
      isExpired: true,
      status: 'expired',
      statusText: `Venció ${expiredAgo}`,
      expiredAgoText: `Este enlace caducó ${expiredAgo}${formattedDate ? ` (${formattedDate})` : ''}.`,
      formattedExpiresAt: formattedDate,
    };
  }

  const remaining = getRemainingTimeLabel(link.expiresAt);
  return {
    isExpired: false,
    status: 'valid',
    statusText: remaining.text,
    remainingText: remaining.text,
  };
}

export function generateSecureToken(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  }
  return 'app_' + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
}

export function buildDirectDownloadUrl(token: string): string {
  const origin = typeof window !== 'undefined' && window.location.origin
    ? window.location.origin
    : '';
  return `${origin}/descargar-app?token=${token}`;
}
